import React, { useEffect, useState } from 'react';
import { Gift, Sparkles, Loader2, Coins, X, Package, AlertTriangle, Box } from 'lucide-react';
import { LootTable } from '../types';
import { fetchMysteryBoxes, openMysteryBox, MysteryBoxResult } from '../services/rewardService';
import { fetchReputation } from '../services/reputationService';

const reasonText: Record<string, string> = {
  INSUFFICIENT_POINTS: 'Not enough points to open this box.',
  OUT_OF_STOCK: 'This box is out of stock right now.',
  NOT_A_MYSTERY_BOX: 'This box is not openable.',
};

const MysteryBoxView: React.FC = () => {
  const [boxes, setBoxes] = useState<LootTable[]>([]);
  const [points, setPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState<string | null>(null);
  const [won, setWon] = useState<MysteryBoxResult['item'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const [b, rep] = await Promise.all([fetchMysteryBoxes(), fetchReputation()]);
      setBoxes(b);
      setPoints(rep.score);
    } catch (e) {
      console.error('Failed to load mystery boxes', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stock = (t: LootTable) => (t.items || []).reduce((s, i) => s + (i.remaining_qty || 0), 0);

  const open = async (t: LootTable) => {
    setError(null);
    setOpening(t.id);
    try {
      const res = await openMysteryBox(t.id);
      if (res.ok && res.item) {
        setWon(res.item);
        if (typeof res.balance === 'number') setPoints(res.balance);
        await load();
      } else {
        setError(reasonText[res.reason || ''] || 'Could not open this box.');
      }
    } catch (e: any) {
      setError(e?.message || 'Could not open this box.');
    } finally {
      setOpening(null);
    }
  };

  if (loading) {
    return (
      <div className="py-24 flex flex-col items-center justify-center gap-4 text-slate-500">
        <Loader2 className="animate-spin text-indigo-500" size={40} />
        <p className="animate-pulse text-sm">Loading mystery boxes…</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
            <Gift className="text-indigo-400" size={26} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white tracking-tight">Mystery Boxes</h1>
            <p className="text-slate-400 text-sm mt-0.5">Spend the points you've earned to open a box and win a reward.</p>
          </div>
        </div>
        <div className="text-right shrink-0 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2">
          <p className="text-2xl font-bold text-white leading-none flex items-center gap-1.5"><Coins size={18} className="text-yellow-400" />{points}</p>
          <p className="text-[10px] uppercase tracking-wider text-slate-500">your points</p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-3 bg-red-900/20 border border-red-500/40 text-red-200 px-4 py-3 rounded-xl text-sm">
          <AlertTriangle size={16} className="shrink-0 text-red-400" />{error}
        </div>
      )}

      {boxes.length === 0 ? (
        <div className="py-16 text-center text-slate-500 border border-dashed border-slate-800 rounded-xl">
          <Box size={40} className="mx-auto mb-3 opacity-40" />
          No mystery boxes are available yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {boxes.map((t) => {
            const inStock = stock(t);
            const cost = t.point_cost ?? 100;
            const canOpen = points >= cost && inStock > 0 && opening === null;
            return (
              <div key={t.id} className="bg-slate-800 rounded-2xl border border-slate-700 p-5 shadow-lg flex flex-col">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-11 w-11 rounded-xl bg-indigo-900/30 border border-indigo-500/30 flex items-center justify-center">
                    <Gift className="text-indigo-300" size={22} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-white font-bold truncate flex items-center gap-2">
                      {t.name}
                      {t.is_community && <span className="text-[9px] uppercase tracking-wider bg-indigo-900/40 text-indigo-300 border border-indigo-500/30 px-1.5 py-0.5 rounded">Community</span>}
                    </h3>
                    {t.description && <p className="text-xs text-slate-500 truncate">{t.description}</p>}
                  </div>
                </div>

                {/* possible rewards */}
                <div className="flex flex-wrap gap-1.5 my-3">
                  {(t.items || []).map((i) => (
                    <span key={i.id} className={`text-[11px] px-2 py-1 rounded-lg border ${i.remaining_qty > 0 ? 'bg-slate-900/40 border-slate-700 text-slate-300' : 'bg-slate-900/20 border-slate-800 text-slate-600 line-through'}`}>
                      {i.name}
                    </span>
                  ))}
                </div>

                <div className="mt-auto flex items-center justify-between pt-3 border-t border-slate-700/60">
                  <span className="text-xs text-slate-500 flex items-center gap-1"><Package size={12} /> {inStock} in stock</span>
                  <button
                    onClick={() => open(t)}
                    disabled={!canOpen}
                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-bold px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    {opening === t.id ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
                    Open · {cost} pts
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reveal modal */}
      {won && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 animate-in fade-in" onClick={() => setWon(null)}>
          <div className="bg-slate-800 rounded-2xl border border-indigo-500/40 shadow-2xl p-8 max-w-sm w-full text-center relative animate-in zoom-in" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setWon(null)} className="absolute top-3 right-3 text-slate-500 hover:text-white"><X size={18} /></button>
            <Sparkles className="mx-auto text-yellow-400 mb-3" size={40} />
            <p className="text-xs uppercase tracking-wider text-indigo-300 font-bold">You won</p>
            {won.image_url ? (
              <img src={won.image_url} alt={won.name} className="w-28 h-28 object-cover rounded-xl mx-auto my-4 border border-slate-700" />
            ) : (
              <div className="w-28 h-28 rounded-xl mx-auto my-4 bg-slate-900 border border-slate-700 flex items-center justify-center"><Gift size={44} className="text-indigo-400" /></div>
            )}
            <h3 className="text-xl font-bold text-white">{won.name}</h3>
            <p className="text-slate-400 text-sm mt-1">{won.type}{won.value ? ` · ~${won.value}` : ''}</p>
            <p className="text-[11px] text-slate-500 mt-3">Find it in your gifts to claim or arrange delivery.</p>
            <button onClick={() => setWon(null)} className="mt-5 w-full bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-2.5 rounded-lg">Nice!</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MysteryBoxView;
